import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';
import { useBrandFontFamilies } from '@src/hooks/useBrandFontFamilies';
import { useFeatureFlags } from '@src/stores-v2/StoreContext';
import BoldIcon from '@src/svgs/BoldIcon';
import CapitalizeIcon from '@src/svgs/CapitalizationIcon';
import DeleteIcon from '@src/svgs/DeleteIcon';
import EllipsisIcon from '@src/svgs/EllipsisIcon';
import ItalicIcon from '@src/svgs/ItalicIcon';
import LeftAlignIcon from '@src/svgs/LeftAlignIcon';
import MiddleAlignIcon from '@src/svgs/MiddleAlignIcon';
import PencilIcon from '@src/svgs/PencilIcon';
import RightAlignIcon from '@src/svgs/RightAlignIcon';
import TextColorIcon from '@src/svgs/TextColorIcon';
import { Bold, BoldValues } from '@src/utility/fonts';
import {
  FONT_SIZE_DEFAULT,
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
  FONTS,
  JAPANESE_FONTS,
} from '@src/utility/general';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { TRANSPARENT_COLOR } from '../ColorDropdownSelect';
import ColorPicker from '../ColorPicker';
import { isBarTarget } from './barGate';
import { useFloatingToolbarHost } from './FloatingToolbarHost';
import {
  FloatingToolbarSurface,
  ToolbarDivider,
  ToolbarIconButton,
  ToolbarTextButton,
} from './FloatingToolbarSurface';
import { ToolbarLayer } from './ToolbarLayer';
import { ToolbarStepper } from './ToolbarStepper';
import { useFloatingToolbarPosition } from './useFloatingToolbarPosition';

import type { FontFamily } from '@src/types.ts/story';

// Font-size bounds for the stepper — shared with the settings-panel Font Size
// fields so both surfaces clamp identically. Outside these, the user has to
// type a value directly in the panel.
const MIN_FONT_SIZE = FONT_SIZE_MIN;
const MAX_FONT_SIZE = FONT_SIZE_MAX;

const TextFloatingToolbar = observer(() => {
  // PLAT-12101 — the surface's host; no host, no bar.
  const host = useFloatingToolbarHost();
  const fontPickerRef = useRef<HTMLDivElement>(null);
  const [fontPickerPos, setFontPickerPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [colorPickerRect, setColorPickerRect] = useState<DOMRect | null>(null);
  const [fontSearch, setFontSearch] = useState('');

  const activeElement = host?.getActiveElement();
  // A single, top-level text that is not a caption, not while playing
  // (PLAT-12098 DE1 / DE2). The selection survives play (PLAT-11402), so the
  // bar has to opt out; a text inside a graphics card is the card's chip's to
  // edit; captions get their own bar (PLAT-12100).
  const isTextEl =
    !!host &&
    activeElement?.source?.type === 'text' &&
    Number(activeElement.track) !== KARAOKE_TRACK_NUMBER &&
    isBarTarget(host);
  const { toolbarRef, pos } = useFloatingToolbarPosition(
    isTextEl,
    activeElement?.source?.id,
  );

  // Sync textProducer config when element changes
  useEffect(() => {
    if (isTextEl && activeElement?.source) {
      host?.textProducer.setTextConfig(activeElement.source);
    }
  }, [isTextEl, activeElement?.source?.id]);

  // Close font picker on outside click
  useEffect(() => {
    if (!fontPickerPos) return;
    const handler = (e: MouseEvent) => {
      if (!fontPickerRef.current?.contains(e.target as Node)) {
        setFontPickerPos(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [fontPickerPos]);

  // Close font picker when element changes
  useEffect(() => {
    setFontPickerPos(null);
  }, [activeElement?.source?.id]);

  // Reset the font search whenever the picker closes so it reopens clean.
  useEffect(() => {
    if (!fontPickerPos) setFontSearch('');
  }, [fontPickerPos]);

  const fontFamilies = useBrandFontFamilies();

  // When the "Hide Arbor Fonts" flag is on, only brand/custom fonts are
  // offered — the curated popular (Arbor) font list is suppressed entirely.
  const { creatorStudioFontsHideArborFonts } = useFeatureFlags();

  const customFamilyNames = useMemo(
    () => new Set(fontFamilies.map(f => f.name)),
    [fontFamilies],
  );
  // The floating toolbar only targets free-form text (it excludes the karaoke
  // track above), so the Japanese fonts are always offered — not gated on the
  // transcript language the way captions are (PLAT-10420).
  const popularFonts = useMemo(() => {
    if (creatorStudioFontsHideArborFonts) return [];
    return [...FONTS, ...JAPANESE_FONTS].filter(
      name => !customFamilyNames.has(name),
    );
  }, [customFamilyNames, creatorStudioFontsHideArborFonts]);

  // Case-insensitive substring filter on the family name (mirrors
  // FontFamilyVariantDropdown's search).
  const fontQuery = fontSearch.trim().toLowerCase();
  const filteredBrandFamilies = useMemo(
    () =>
      fontQuery
        ? fontFamilies.filter(f => f.name.toLowerCase().includes(fontQuery))
        : fontFamilies,
    [fontQuery, fontFamilies],
  );
  const filteredPopularFonts = useMemo(
    () =>
      fontQuery
        ? popularFonts.filter(n => n.toLowerCase().includes(fontQuery))
        : popularFonts,
    [fontQuery, popularFonts],
  );

  const config = host?.textProducer.getTextConfig();
  const currentVariantStyle = useMemo(() => {
    const ps = config?.font_family as string | undefined;
    if (!ps) return null;
    for (const f of fontFamilies) {
      const v = f.variants.find(v => v.postScriptName === ps);
      if (v) return v.style;
    }
    return null;
  }, [config?.font_family, fontFamilies]);

  if (!host || !isTextEl) return null;
  const { textProducer, settings, editText } = host;

  const fontFamily = (config?.font_family || 'Inter').split(',')[0].trim();
  // The stepper reads the element itself, not `config`: setTextConfig stands
  // in 32 for an unset size, so auto-sized text read "32" and the first click
  // jumped it (PLAT-12088). convertToPx is exact and, for auto-sized text,
  // falls back to the renderer's computed size. Being an observer, this
  // re-reads on every selection or outside change.
  const readLiveFontSizePx = () => {
    if (!activeElement?.source) return null;
    return textProducer.convertToPx(
      activeElement.source.font_size,
      activeElement.source.id,
    );
  };
  const readStepperFontSize = () =>
    Math.round(readLiveFontSizePx() ?? FONT_SIZE_DEFAULT);
  const fontSize = readStepperFontSize();
  const fillColor =
    typeof config?.fill_color === 'string' ? config.fill_color : '#ffffff';
  // 600+ counts as "bold" here so the button reflects the actually-applied
  // variant weight after a click. The variant-find logic below also uses
  // ≥600 as the band threshold — keep both in sync, otherwise a SemiBold
  // swap (weight 600) would leave the button visually OFF.
  const isBold = Number(config?.font_weight) >= 600;
  const isItalic =
    currentVariantStyle === 'italic' || config?.font_style === 'italic';
  const isCapitalized = config?.text_transform === 'uppercase';
  const alignment = (config?.x_alignment as string) || '0%';

  // ─── Canva-style B/I synthesis tracking ─────────────────────────────────
  // Buttons are always clickable. When a real variant of the current
  // family exists, we swap font_family to that variant; when none exists,
  // we fall back to CSS-level font_weight / font_style so the click still
  // produces a visible change, with a small amber dot on the button to
  // signal "synthetic — final render may differ".
  const currentPostScript = config?.font_family as string | undefined;
  const currentFamily = fontFamilies.find(f =>
    f.variants.some(v => v.postScriptName === currentPostScript),
  );
  const currentVariant = currentFamily?.variants.find(
    v => v.postScriptName === currentPostScript,
  );
  const currentVariantWeight =
    currentVariant?.weight ?? (Number(config?.font_weight) || 400);
  const currentTrackStyle: 'normal' | 'italic' =
    currentVariant?.style ??
    (config?.font_style === 'italic' ? 'italic' : 'normal');
  // Popular Google Fonts (curated FONTS list) ship full weight/italic axes,
  // so we trust them — no synth indicator there.
  const isPopularFont = !currentFamily;
  // "Synthetic" = the style is applied but the rendered font face isn't
  // actually that style/weight, so the renderer has to approximate via
  // CSS-level font-weight/font-style. We mark it for brand-kit fonts only;
  // Google Fonts via the curated list are trusted.
  const isBoldSynthetic =
    isBold && !isPopularFont && currentVariantWeight < 600;
  const isItalicSynthetic =
    isItalic && !isPopularFont && currentVariantStyle !== 'italic';

  const modify = (patch: Record<string, unknown>) => {
    // font_size is stored as display px in config but Creatomate needs vh
    const expanded: Record<string, unknown> = { ...patch };
    if ('font_size' in expanded) {
      expanded.font_size = textProducer.convertToVh(
        String(expanded.font_size),
        activeElement?.source?.id,
      );
    }
    const editorStore = host.editorStore;
    if (editorStore?.editor && activeElement?.source?.id) {
      editorStore.editor.commandUpdateElement(
        activeElement.source.id,
        expanded,
      );
      textProducer.modifyProperty(patch, false, null);
    } else {
      textProducer.modifyProperty(patch);
    }
  };

  // PLAT-12098 DS3 — saved right after the command, as the graphics card
  // chip's Delete is, so the two Deletes never differ after a reload.
  const handleDelete = () => {
    const editor = host.editorStore?.editor;
    if (!editor || !activeElement?.source?.id) return;
    editor.commandRemoveElement(activeElement.source.id);
    host.persist();
  };

  // More toggles Graphics > Text, where selection routes a top-level text
  // (PLAT-12098 DE5), through the host (PLAT-12101); no settings, no More.
  const isSidebarOnThisElement = !!settings?.isOpen('text');

  const handleSelectFont = (name: string, family: FontFamily | undefined) => {
    if (family) {
      const currentWeight = Number(config?.font_weight) || 400;
      const active = family.variants.filter(v => !v.archivedAt);
      const best = active.sort(
        (a, b) =>
          Math.abs(a.weight - currentWeight) -
          Math.abs(b.weight - currentWeight),
      )[0];
      if (best)
        modify({
          font_family: best.postScriptName,
          font_weight: best.weight.toString(),
        });
    } else {
      modify({ font_family: name });
    }
    setFontPickerPos(null);
  };

  const toolbar = (
    <FloatingToolbarSurface ref={toolbarRef} pos={pos}>
      {/* Edit — the text's content, in the quick-edit popover the chip's
       * pencil opened before the bar replaced the chip. Only where the host
       * offers it: the clip preview, which has no panel to edit it in. */}
      {editText && (
        <>
          <ToolbarIconButton
            title="Edit text"
            aria-label="Edit text"
            onClick={e => editText(e.currentTarget)}
          >
            <PencilIcon width="14" height="14" strokeColor="currentColor" />
          </ToolbarIconButton>
          <ToolbarDivider />
        </>
      )}

      {/* Font family */}
      <SFontName
        title={fontFamily}
        isActive={!!fontPickerPos}
        onClick={e => {
          if (fontPickerPos) {
            setFontPickerPos(null);
          } else {
            const r = e.currentTarget.getBoundingClientRect();
            setFontPickerPos({ top: r.bottom + 6, left: r.left });
          }
        }}
      >
        <SFontLabel>{fontFamily}</SFontLabel>
      </SFontName>

      <ToolbarDivider />

      {/* Size — one step per click, hold to repeat, bounded to the panel's
       * Font Size range (shared ToolbarStepper, PLAT-12099). */}
      <ToolbarStepper
        value={fontSize}
        min={MIN_FONT_SIZE}
        max={MAX_FONT_SIZE}
        step={1}
        onChange={next => modify({ font_size: next })}
        decreaseLabel="Decrease font size"
        increaseLabel="Increase font size"
      >
        {fontSize}
      </ToolbarStepper>

      <ToolbarDivider />

      {/* Color swatch */}
      <ToolbarIconButton
        title="Text color"
        aria-label="Text color"
        isActive={!!colorPickerRect}
        onClick={e => {
          if (colorPickerRect) {
            setColorPickerRect(null);
          } else {
            setColorPickerRect(e.currentTarget.getBoundingClientRect());
          }
        }}
      >
        <TextColorIcon />
      </ToolbarIconButton>
      {/* ColorPicker rendered via separate portal — see return below */}

      <ToolbarDivider />

      {/* B / I / U — Canva-style: always interactive. Swap to a real
       * variant when one exists in the current family; fall back to CSS
       * font_weight / font_style otherwise. A small amber dot signals
       * "synthetic — final render may differ" without disabling the
       * button (the user can still apply the style and decide what to do). */}
      <ToolbarIconButton
        isActive={isBold}
        title={
          isBoldSynthetic
            ? `Synthetic bold — no bold variant in ${currentFamily?.name ?? 'this font'}. Upload one in Brand Kit Fonts for a true bold.`
            : 'Bold'
        }
        onClick={() => {
          const targetBold = !isBold;
          // Brand-kit path: snap to a real variant of the current style
          // track (normal/italic) at the target weight band.
          if (currentFamily) {
            const isInBand = targetBold
              ? (w: number) => w >= 600
              : (w: number) => w < 600;
            const realMatch = currentFamily.variants.find(
              v =>
                !v.archivedAt &&
                isInBand(v.weight) &&
                v.style === currentTrackStyle,
            );
            if (realMatch) {
              modify({
                font_family: realMatch.postScriptName,
                font_weight: realMatch.weight.toString(),
              });
              return;
            }
          }
          // Synthesis fallback — keep the current font_family, CSS handles it.
          modify({
            font_weight: targetBold
              ? '700'
              : BoldValues[Bold.normal].toString(),
          });
        }}
        aria-label="Bold"
        aria-pressed={isBold}
      >
        <BoldIcon
          fillColor={isBold ? 'var(--foreground)' : 'var(--text-muted)'}
        />
        {isBoldSynthetic && <SSynthDot aria-hidden />}
      </ToolbarIconButton>

      <ToolbarIconButton
        isActive={isItalic}
        title={
          isItalicSynthetic
            ? `Synthetic italic — no italic variant in ${currentFamily?.name ?? 'this font'}. Upload one in Brand Kit Fonts for a true italic.`
            : 'Italic'
        }
        onClick={() => {
          const targetStyle: 'normal' | 'italic' = isItalic
            ? 'normal'
            : 'italic';
          // Brand-kit path: find a same-weight (or closest) variant in the
          // target style track.
          if (currentFamily) {
            const candidates = currentFamily.variants.filter(
              v => !v.archivedAt && v.style === targetStyle,
            );
            if (candidates.length > 0) {
              const best = [...candidates].sort(
                (a, b) =>
                  Math.abs(a.weight - currentVariantWeight) -
                  Math.abs(b.weight - currentVariantWeight),
              )[0];
              modify({
                font_family: best.postScriptName,
                font_weight: best.weight.toString(),
                font_style: targetStyle,
              });
              return;
            }
          }
          // Synthesis fallback — CSS font_style only.
          modify({ font_style: targetStyle });
        }}
        aria-label="Italic"
        aria-pressed={isItalic}
      >
        <ItalicIcon
          fillColor={isItalic ? 'var(--foreground)' : 'var(--text-muted)'}
        />
        {isItalicSynthetic && <SSynthDot aria-hidden />}
      </ToolbarIconButton>

      {/* Underline button intentionally omitted — Creatomate's text
       * renderer has no underline / text-decoration property, so the
       * button could never actually underline the text. See the spike
       * trace in PLAT-9972's history (commits 4840ddf18 / 5599e5f64)
       * for the U+0332 workaround that was tried and rolled back. */}

      <ToolbarDivider />

      {/* Case */}
      <ToolbarIconButton
        isActive={isCapitalized}
        onClick={() =>
          modify({
            text_transform: isCapitalized ? 'none' : 'uppercase',
          })
        }
        aria-label="Uppercase"
        aria-pressed={isCapitalized}
      >
        <CapitalizeIcon
          fillColor={isCapitalized ? 'var(--foreground)' : 'var(--text-muted)'}
        />
      </ToolbarIconButton>

      <ToolbarDivider />

      {/* Alignment */}
      <ToolbarIconButton
        isActive={alignment === '0%'}
        onClick={() => modify({ x_alignment: '0%' })}
        aria-label="Align left"
        aria-pressed={alignment === '0%'}
      >
        <LeftAlignIcon
          fillColor={
            alignment === '0%' ? 'var(--foreground)' : 'var(--text-muted)'
          }
        />
      </ToolbarIconButton>
      <ToolbarIconButton
        isActive={alignment === '50%'}
        onClick={() => modify({ x_alignment: '50%' })}
        aria-label="Align center"
        aria-pressed={alignment === '50%'}
      >
        <MiddleAlignIcon
          strokeColor={
            alignment === '50%' ? 'var(--foreground)' : 'var(--text-muted)'
          }
        />
      </ToolbarIconButton>
      <ToolbarIconButton
        isActive={alignment === '100%'}
        onClick={() => modify({ x_alignment: '100%' })}
        aria-label="Align right"
        aria-pressed={alignment === '100%'}
      >
        <RightAlignIcon
          fillColor={
            alignment === '100%' ? 'var(--foreground)' : 'var(--text-muted)'
          }
        />
      </ToolbarIconButton>

      <ToolbarDivider />

      {/* Delete */}
      <ToolbarIconButton aria-label="Delete" onClick={handleDelete}>
        <DeleteIcon width="15" height="15" strokeColor="currentColor" />
      </ToolbarIconButton>

      {/* More — toggle the full settings panel */}
      {settings && (
        <ToolbarIconButton
          aria-label="More options"
          isActive={isSidebarOnThisElement}
          onClick={() => settings.toggle('text')}
        >
          <EllipsisIcon width="16" height="4" fillColor="currentColor" />
        </ToolbarIconButton>
      )}
    </FloatingToolbarSurface>
  );

  // A sibling of the toolbar, never a child: the shared toolbar surface's fade-in ends at
  // transform: scale(1), which would make it the containing block for a
  // position: fixed descendant.
  const fontPicker = fontPickerPos ? (
    <SFontPicker
      ref={fontPickerRef}
      style={{ top: fontPickerPos.top, left: fontPickerPos.left }}
    >
      <SFontSearchInput
        autoFocus
        placeholder="Search"
        value={fontSearch}
        onChange={e => setFontSearch(e.target.value)}
      />
      <SFontList>
        {filteredBrandFamilies.length > 0 && (
          <>
            <SFontSection>Brand Fonts</SFontSection>
            {filteredBrandFamilies.map(f => {
              const isSelected = f.variants.some(
                v => v.postScriptName === config?.font_family,
              );
              return (
                <SFontOption
                  key={f.name}
                  fontFamily={f.name}
                  isSelected={isSelected}
                  onClick={() => handleSelectFont(f.name, f)}
                >
                  {f.name}
                </SFontOption>
              );
            })}
            {filteredPopularFonts.length > 0 && <SFontDivider />}
          </>
        )}
        {filteredPopularFonts.length > 0 && (
          <>
            <SFontSection>Popular Fonts</SFontSection>
            {filteredPopularFonts.map(name => (
              <SFontOption
                key={name}
                fontFamily={name}
                isSelected={fontFamily === name}
                onClick={() => handleSelectFont(name, undefined)}
              >
                {name}
              </SFontOption>
            ))}
          </>
        )}
        {filteredBrandFamilies.length === 0 &&
          filteredPopularFonts.length === 0 && (
            <SFontEmpty>No fonts match "{fontSearch}"</SFontEmpty>
          )}
      </SFontList>
    </SFontPicker>
  ) : null;

  // ColorPicker portals itself to document.body and computes its own placement,
  // so it's safe to render inline even though the toolbar surface's fade-in animation has
  // transform: scale(1) (which would otherwise create a containing block that
  // traps position: fixed children).
  const colorPicker = colorPickerRect ? (
    <ColorPicker
      pos={colorPickerRect}
      defaultValue={fillColor}
      solidDefaultAlpha
      getValue={color => {
        // Erase sends null; apply transparent, as the Text panel does.
        modify({ fill_color: color ?? TRANSPARENT_COLOR });
      }}
      onClose={() => setColorPickerRect(null)}
    />
  ) : null;

  return (
    <ToolbarLayer>
      {toolbar}
      {fontPicker}
      {colorPicker}
    </ToolbarLayer>
  );
});

export default TextFloatingToolbar;

// ─── styled components ───────────────────────────────────────────────────────

// The family name, clipped to a readable width inside the shared text button.
const SFontName = styled(ToolbarTextButton)`
  max-width: 120px;
`;

const SFontLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SFontPicker = styled.div`
  position: fixed;
  /* One above the toolbar surface (100), in the same stage-local scale. */
  z-index: 101;
  width: 200px;
  max-height: 320px;
  display: flex;
  flex-direction: column;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 6px;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.4),
    0 2px 6px rgba(0, 0, 0, 0.2);
`;

const SFontSearchInput = styled.input`
  /* border-box so width:100% accounts for the input's own padding + border
   * and the right edge stays inside the picker's rounded corner. */
  box-sizing: border-box;
  width: 100%;
  height: 32px;
  min-height: 32px;
  flex-shrink: 0;
  padding: 0 10px;
  margin-bottom: 6px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: transparent;
  color: var(--foreground);
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  outline: none;
  &:focus {
    border-color: var(--accent);
  }
  &::placeholder {
    color: var(--text-muted);
  }
`;

const SFontList = styled.div`
  /* The search field above keeps its height; this list absorbs the clamp
   * and scrolls instead. */
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--border) transparent;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--border);
    border-radius: 2px;
  }
`;

const SFontEmpty = styled.div`
  padding: 10px;
  color: var(--text-muted);
  font-size: var(--text-sm);
  text-align: center;
`;

const SFontSection = styled.span`
  display: block;
  padding: 6px 6px 2px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
  color: var(--text-muted);
  letter-spacing: 0.4px;
`;

const SFontDivider = styled.div`
  height: 1px;
  background: var(--border);
  margin: 4px 0;
`;

const SFontOption = styled.button<{ fontFamily: string; isSelected: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 6px 10px;
  border-radius: 6px;
  border: none;
  background: ${({ isSelected }) =>
    isSelected ? 'color-mix(in srgb, var(--accent) 12%, transparent)' : 'none'};
  color: ${({ isSelected }) =>
    isSelected ? 'var(--accent)' : 'var(--text-secondary)'};
  font-family: ${({ fontFamily }) => fontFamily}, Inter;
  font-size: var(--text-sm);
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover {
    background: ${({ isSelected }) =>
      isSelected
        ? 'color-mix(in srgb, var(--accent) 18%, transparent)'
        : 'var(--surface-elevated)'};
  }
`;

/**
 * Small amber dot anchored to the top-right of a ToolbarIconButton. Signals that
 * the applied B/I style is synthesised via CSS (no real variant exists in
 * the current font family) — so the in-app preview may approximate the
 * effect, and the final rendered video may not honour it. Compact,
 * non-blocking, mirrors Canva's quiet "approximated styling" indicator.
 */
const SSynthDot = styled.span`
  position: absolute;
  top: 3px;
  right: 3px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--warning, #f59e0b);
  pointer-events: none;
`;
